import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Stack } from '@mui/material';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { userInviteActions } from '_actions';
import { getCurrentUser } from '_helpers';
import { userInviteStatus } from '_constants';
import PageContainer from 'components/v2/common/PageContainer';
import importedStyles from './Utilities/styles';
import UsersInviteModal, { useUsersInviteModal } from './UsersInviteModal';
import UserInviteTable from '../UserManagement/UserInvite/UserInviteTable';
import useInviteListFetch from '../UserManagement/Utilities/hooks/useInviteListFetch';
import BasicTextField from '../../common/BasicTextField';
import { BasicPopover } from '../../common/BasicPopover';
import FilterButton from '../../common/Filters/FilterUtililties/FilterButton';
import InviteStatusFilter from '../../common/Filters/UserInviteFilter/InviteStatusFilter';
import BasicButton from '../../common/BasicButton';
import TextButton from '../../common/TextButton';
import './Users.css';

import { MetricsDetailFooter as Footer } from '../MetricsDetail/Sections';
import BasicActionButton from '../../../v3/common/BasicActionButton';

const INVITE_PAGE_SIZE = 10;

export default function Users() {
	const dispatch = useDispatch();

	const currentUser = getCurrentUser();
	const organizationId = currentUser?.organization_id || null;

	const { initiateUsersInviteModal, modalProps } = useUsersInviteModal();

	const [invitePage, setInvitePage] = useState(0);
	const [inviteSearchField, setInviteSearchField] = useState('');
	const [inviteSearch, setInviteSearch] = useState('');
	const [inviteStatuses, setInviteStatuses] = useState([]);

	const invitePayload = useMemo(
		() => ({
			...(organizationId && { organization: organizationId }),
			...(inviteStatuses.length && { inviteStatus: inviteStatuses.join(',') }),
			...(inviteSearch && { search: inviteSearch }),
			offset: invitePage * INVITE_PAGE_SIZE,
		}),
		[organizationId, inviteStatuses, inviteSearch, invitePage]
	);

	const {
		rows: inviteRows,
		columns: inviteColumns,
		totalCount: inviteTotalCount,
		loading: inviteLoading,
	} = useInviteListFetch(invitePayload);

	const refreshInvites = useCallback(() => {
		dispatch(userInviteActions.list(invitePayload));
	}, [dispatch, invitePayload]);

	const handleInviteCreated = useCallback(() => {
		refreshInvites();
	}, [refreshInvites]);

	const handleInvitePageChange = (_event, nextPage) => {
		setInvitePage(nextPage);
	};

	const handleInviteSearchChange = (event) => {
		setInviteSearchField(event.target.value);
	};

	const triggerInviteSearch = () => {
		setInvitePage(0);
		setInviteSearch(inviteSearchField.trim());
	};

	const clearInviteSearch = () => {
		if (!inviteSearchField && !inviteSearch) return;
		setInviteSearchField('');
		setInviteSearch('');
		setInvitePage(0);
	};

	const handleInviteSearchKeyDown = (event) => {
		if (event.keyCode === 13) triggerInviteSearch();
	};

	const handleInviteStatusChange = (statuses) => {
		setInvitePage(0);
		setInviteStatuses(statuses);
	};

	return (
		<Box sx={{ backgroundColor: '#EDEDE4' }}>
			<PageContainer size={'large'} backgroundColor={'#EDEDE4'}>
				<Box sx={importedStyles.root}>
					<Box sx={importedStyles.headingBox}>
						<Box component={'h1'} sx={{ ...importedStyles.h1, mb: 0 }}>
							Users
						</Box>

						<BasicActionButton
							label={'Invite user'}
							action={initiateUsersInviteModal}
						/>
					</Box>

					<Box sx={styles.content}>
						<Stack
							direction={{ xs: 'column', md: 'row' }}
							sx={styles.invitesHeader}
							spacing={2}
						>
							<Box sx={styles.invitesFilters}>
								<Box sx={{ width: { xs: '100%', sm: '390px' } }}>
									<BasicTextField
										name={'inviteSearch'}
										value={inviteSearchField}
										startIcon={<SearchIcon sx={styles.icon} />}
										endIcon={<ClearIcon sx={styles.clearIcon} />}
										placeholder={'Email or Invited By - press Enter to search'}
										handleChange={handleInviteSearchChange}
										handleIconAction={clearInviteSearch}
										onKeyDown={handleInviteSearchKeyDown}
										sx={styles.searchField}
									/>
								</Box>
								<Box sx={{ width: '150px' }}>
									<InviteStatusFilterPopover
										selectedStatuses={inviteStatuses}
										onApply={handleInviteStatusChange}
									/>
								</Box>
							</Box>
						</Stack>

						<UserInviteTable
							rows={inviteRows}
							columns={inviteColumns}
							totalCount={inviteTotalCount}
							page={invitePage}
							loading={inviteLoading}
							handlePageChange={handleInvitePageChange}
							clickable={false}
						/>
					</Box>
				</Box>
			</PageContainer>

			<UsersInviteModal {...modalProps} onInviteCreated={handleInviteCreated} />

			<Footer />
		</Box>
	);
}

function InviteStatusFilterPopover({ selectedStatuses, onApply }) {
	const ref = useRef();
	const allStatusValues = Object.values(userInviteStatus);
	const statusObjArr = allStatusValues.map((status) => ({
		name: status.charAt(0) + status.slice(1).toLowerCase(),
		value: status,
	}));

	const [checked, setChecked] = useState(selectedStatuses);

	useEffect(() => {
		setChecked(selectedStatuses);
	}, [selectedStatuses]);

	const applyFilters = () => {
		onApply(checked);
		ref.current?.close();
	};

	const resetFilters = () => {
		setChecked([]);
		onApply([]);
		ref.current?.close();
	};

	return (
		<BasicPopover
			ref={ref}
			id={'users-invite-status-filter'}
			ButtonComponent={FilterButton}
		>
			<Box sx={styles.statusPopoverRoot}>
				<Box sx={styles.statusPopoverBody}>
					<Box sx={styles.statusPopoverTitle}>Filter by</Box>
					<InviteStatusFilter
						options={statusObjArr}
						checkedSecondaryOptions={checked}
						setCheckedSecondaryOptions={setChecked}
					/>
				</Box>
				<Box sx={styles.statusPopoverFooter}>
					<Box>
						<TextButton
							label={'Reset filters'}
							action={resetFilters}
							rootStyle={{ fontSize: '14px', height: '18px' }}
						/>
					</Box>
					<Box>
						<BasicButton
							style={{ fontSize: '14px' }}
							action={applyFilters}
							label={'Apply filters'}
						/>
					</Box>
				</Box>
			</Box>
		</BasicPopover>
	);
}

const styles = {
	content: {
		mt: 4,
	},
	invitesHeader: {
		justifyContent: 'flex-start',
		alignItems: { xs: 'flex-start', md: 'center' },
		mb: 2,
	},
	invitesFilters: {
		display: 'flex',
		gap: 2,
		alignItems: 'center',
		height: '40px',
	},
	searchField: {
		backgroundColor: '#fff',
		borderRadius: '10px',
		height: '40px',
	},
	icon: {
		color: '#495D5E',
	},
	clearIcon: {
		fontSize: '18px',
		flexShrink: 0,
		color: '#495D5E',
		pointerEvents: 'none',
	},
	statusPopoverRoot: {
		minWidth: '340px',
	},
	statusPopoverBody: {
		p: '20px 20px 7px 20px',
		borderBottom: '1px solid #B6BEBF',
	},
	statusPopoverTitle: {
		mb: '16px',
		color: '#0C2728',
		fontFamily: 'LarsseitMedium',
		fontSize: '14px',
	},
	statusPopoverFooter: {
		p: '16px 20px',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
	},
};
